import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { collection, addDoc, serverTimestamp, doc, getDoc } from 'firebase/firestore';
import { signOut } from 'firebase/auth';
import { auth, db } from '../firebase';
import './ApplicationForm.css';

function ApplicationForm() {
  const location = useLocation();
  const navigate = useNavigate();
  const user = auth.currentUser;

  // Get product data from navigation state
  const navigationProduct = location.state || {};
  
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [formData, setFormData] = useState({
    productId: navigationProduct.productId || '',
    productName: navigationProduct.productName || '',
    firstName: '',
    lastName: '',
    age: '',
    location: '',
    occupation: '',
    socialMedia: '',
    previousExperience: '',
    specificInterests: '',
    reason: '',
    experience: '',
    email: '',
    phoneNumber: '',
    ndaAgreed: false,
    marketingConsent: false
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    fetchProductDetails();
  }, []);

  const fetchProductDetails = async () => {
    try {
      // If product was pre-selected, fetch its details
      if (navigationProduct.productId) {
        const productDoc = await getDoc(doc(db, 'products', navigationProduct.productId));
        if (productDoc.exists()) {
          setSelectedProduct({ id: productDoc.id, ...productDoc.data() });
        }
      } else {
        // If no product was selected, redirect back to product browse
        alert('Please select a product to apply for');
        navigate('/apply');
      }
    } catch (error) {
      console.error('Error fetching product:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors({ ...errors, [name]: '' });
    }
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePhoneNumber = (phone) => {
    // Remove all non-digit characters
    const cleanPhone = phone.replace(/\D/g, '');
    // Check if it has 10-15 digits (covers most international formats)
    return cleanPhone.length >= 10 && cleanPhone.length <= 15;
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.productId) {
      newErrors.productId = 'Please select a product';
    }

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }

    if (!formData.age || formData.age < 13 || formData.age > 120) {
      newErrors.age = 'Please enter a valid age (13-120)';
    }

    if (!formData.location.trim()) {
      newErrors.location = 'Location is required';
    }

    if (!formData.reason.trim()) {
      newErrors.reason = 'Please tell us why you want to test this product';
    }

    if (!formData.experience.trim()) {
      newErrors.experience = 'Please describe your experience';
    }

    if (!formData.previousExperience.trim()) {
      newErrors.previousExperience = 'Please describe your previous beta testing experience';
    }

    if (!formData.specificInterests.trim()) {
      newErrors.specificInterests = 'Please share your specific interests';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!validateEmail(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.phoneNumber.trim()) {
      newErrors.phoneNumber = 'Phone number is required';
    } else if (!validatePhoneNumber(formData.phoneNumber)) {
      newErrors.phoneNumber = 'Please enter a valid phone number (10-15 digits)';
    }

    if (!formData.ndaAgreed) {
      newErrors.ndaAgreed = 'You must agree to the NDA to continue';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      alert('Please fix the errors in the form');
      return;
    }

    setLoading(true);

    try {
      await addDoc(collection(db, 'applications'), {
        userId: user.uid,
        userEmail: user.email,
        productId: formData.productId,
        productName: formData.productName,
        firstName: formData.firstName,
        lastName: formData.lastName,
        age: parseInt(formData.age),
        location: formData.location,
        occupation: formData.occupation || 'Not provided',
        socialMedia: formData.socialMedia || 'Not provided',
        previousExperience: formData.previousExperience,
        specificInterests: formData.specificInterests,
        reason: formData.reason,
        experience: formData.experience,
        email: formData.email,
        phoneNumber: formData.phoneNumber,
        ndaAgreed: formData.ndaAgreed,
        marketingConsent: formData.marketingConsent,
        status: 'pending',
        createdAt: serverTimestamp()
      });

      setSuccess(true);
      setFormData({
        productId: '',
        productName: '',
        firstName: '',
        lastName: '',
        age: '',
        location: '',
        occupation: '',
        socialMedia: '',
        previousExperience: '',
        specificInterests: '',
        reason: '',
        experience: '',
        email: '',
        phoneNumber: '',
        ndaAgreed: false,
        marketingConsent: false
      });

      setTimeout(() => {
        navigate('/my-applications');
      }, 2000);
    } catch (error) {
      console.error('Error submitting application:', error);
      alert('Error submitting application. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate('/');
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  if (success) {
    return (
      <div className="application-form">
        <nav className="form-nav">
          <div className="nav-container">
            <div className="logo">BetaBuyers</div>
            <button onClick={handleLogout} className="btn-logout">Sign Out</button>
          </div>
        </nav>
        <div className="form-content">
          <div className="success-message">
            <h2>✓ Application Submitted!</h2>
            <p>Your application has been submitted successfully. Redirecting to your applications...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="application-form">
      <nav className="form-nav">
        <div className="nav-container">
          <div className="logo">BetaBuyers</div>
          <div className="nav-buttons">
            <button onClick={() => navigate('/dashboard')} className="btn-nav">Dashboard</button>
            <button onClick={handleLogout} className="btn-logout">Sign Out</button>
          </div>
        </div>
      </nav>

      <div className="form-content">
        <div className="form-header">
          <h1>Apply to Become a Beta Tester</h1>
        </div>

        <form onSubmit={handleSubmit} className="application-form-container">
          {/* Product Display - Simple */}
          {selectedProduct && (
            <div className="selected-product-display-simple">
              <h2>{selectedProduct.name}</h2>
            </div>
          )}

          {/* Personal Information Section */}
          <div className="form-section-header">
            <h3>Personal Information</h3>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="firstName">First Name *</label>
              <input
                type="text"
                id="firstName"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                disabled={loading}
                placeholder="Enter your first name"
                className={errors.firstName ? 'error' : ''}
              />
              {errors.firstName && <span className="error-message">{errors.firstName}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="lastName">Last Name *</label>
              <input
                type="text"
                id="lastName"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                disabled={loading}
                placeholder="Enter your last name"
                className={errors.lastName ? 'error' : ''}
              />
              {errors.lastName && <span className="error-message">{errors.lastName}</span>}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="age">Age *</label>
              <input
                type="number"
                id="age"
                name="age"
                value={formData.age}
                onChange={handleChange}
                disabled={loading}
                placeholder="Enter your age"
                min="13"
                max="120"
                className={errors.age ? 'error' : ''}
              />
              {errors.age && <span className="error-message">{errors.age}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="location">Location/Country *</label>
              <input
                type="text"
                id="location"
                name="location"
                value={formData.location}
                onChange={handleChange}
                disabled={loading}
                placeholder="e.g., United States, New York"
                className={errors.location ? 'error' : ''}
              />
              {errors.location && <span className="error-message">{errors.location}</span>}
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="occupation">Occupation (Optional)</label>
            <input
              type="text"
              id="occupation"
              name="occupation"
              value={formData.occupation}
              onChange={handleChange}
              disabled={loading}
              placeholder="e.g., Software Engineer, Student, Marketing Manager"
            />
          </div>

          <div className="form-group">
            <label htmlFor="socialMedia">Social Media Handles (Optional)</label>
            <input
              type="text"
              id="socialMedia"
              name="socialMedia"
              value={formData.socialMedia}
              onChange={handleChange}
              disabled={loading}
              placeholder="e.g., @username on Twitter, Instagram, etc."
            />
          </div>

          {/* Contact Information Section */}
          <div className="form-section-header">
            <h3>Contact Information</h3>
          </div>

          <div className="form-group">
            <label htmlFor="email">Email Address *</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              disabled={loading}
              placeholder="your.email@example.com"
              className={errors.email ? 'error' : ''}
            />
            {errors.email && <span className="error-message">{errors.email}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="phoneNumber">Phone Number *</label>
            <input
              type="tel"
              id="phoneNumber"
              name="phoneNumber"
              value={formData.phoneNumber}
              onChange={handleChange}
              disabled={loading}
              placeholder="+1 (555) 123-4567"
              className={errors.phoneNumber ? 'error' : ''}
            />
            {errors.phoneNumber && <span className="error-message">{errors.phoneNumber}</span>}
          </div>

          {/* Experience Section */}
          <div className="form-section-header">
            <h3>Experience & Background</h3>
          </div>

          <div className="form-group">
            <label htmlFor="previousExperience">Previous Beta Testing Experience *</label>
            <textarea
              id="previousExperience"
              name="previousExperience"
              value={formData.previousExperience}
              onChange={handleChange}
              disabled={loading}
              rows="3"
              placeholder="Describe any previous beta testing experience (e.g., tested apps, games, hardware). If none, write 'No previous experience'"
              className={errors.previousExperience ? 'error' : ''}
            />
            {errors.previousExperience && <span className="error-message">{errors.previousExperience}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="specificInterests">Specific Interests *</label>
            <textarea
              id="specificInterests"
              name="specificInterests"
              value={formData.specificInterests}
              onChange={handleChange}
              disabled={loading}
              rows="3"
              placeholder="What are your interests? (e.g., gaming, fitness, productivity, photography)"
              className={errors.specificInterests ? 'error' : ''}
            />
            {errors.specificInterests && <span className="error-message">{errors.specificInterests}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="reason">Why do you want to test this product? *</label>
            <textarea
              id="reason"
              name="reason"
              value={formData.reason}
              onChange={handleChange}
              disabled={loading}
              rows="4"
              placeholder="Tell us why you're interested in testing this specific product..."
              className={errors.reason ? 'error' : ''}
            />
            {errors.reason && <span className="error-message">{errors.reason}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="experience">Your experience with tech products *</label>
            <textarea
              id="experience"
              name="experience"
              value={formData.experience}
              onChange={handleChange}
              disabled={loading}
              rows="4"
              placeholder="Describe your experience with similar products or technologies..."
              className={errors.experience ? 'error' : ''}
            />
            {errors.experience && <span className="error-message">{errors.experience}</span>}
          </div>

          {/* NDA Agreement */}
          <div className="form-section-header">
            <h3>Agreement</h3>
          </div>

          <div className="form-group checkbox-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                name="ndaAgreed"
                checked={formData.ndaAgreed}
                onChange={handleChange}
                disabled={loading}
                className={errors.ndaAgreed ? 'error' : ''}
              />
              <span>I agree to sign an NDA (Non-Disclosure Agreement) and keep all product information confidential *</span>
            </label>
            {errors.ndaAgreed && <span className="error-message">{errors.ndaAgreed}</span>}
          </div>

          <div className="form-group checkbox-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                name="marketingConsent"
                checked={formData.marketingConsent}
                onChange={handleChange}
                disabled={loading}
              />
              <span>I would like to receive emails about new products and beta testing opportunities (Optional)</span>
            </label>
          </div>

          <button type="submit" className="btn-submit-form" disabled={loading}>
            {loading ? 'Submitting...' : 'Submit Application'}
          </button>
        </form>
      </div>
    </div>
  );
}

export default ApplicationForm;

